/**
 * LocalizedCurrencyDisplay - Display currency with user's preferred format
 */

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { useUserPreferences } from '../context';
import { useReportingBaseCurrency } from '../hooks/useReportingBaseCurrency';
import { LtrToken } from './common/LtrToken';
import type { NumberFormat } from '../../generated/config-enums';

/** enum source: shared/config/locale.config.json § numberFormats § values —
 *  aliased rather than re-spelled so the value set cannot drift. */
export type NumberFormatOverride = NumberFormat;

export interface LocalizedCurrencyDisplayProps {
  value: number | null | undefined;
  /**
   * ISO currency code the amount is denominated in. Pass the entity's own
   * `currency_code` (a PO, invoice, price-list line, FX transaction) — verbatim,
   * including `null`.
   *
   * Empty / `null` / `undefined` renders the plain number with no symbol. That
   * covers a create form before a currency is chosen AND a row whose nullable
   * currency column really is NULL (an explicit-NULL import, a pipeline
   * export/import round-trip). Rendering the bare number is the deliberate
   * answer there: a missing currency must never be *invented* as USD — see
   * `tools/check_no_hardcoded_currency_default.py`. Accepting `null` is also
   * what keeps a NULL row from reaching `Intl.NumberFormat`, which throws on a
   * nullish `currency` under `style: 'currency'`.
   *
   * For a **currency-less aggregate/total** (dashboard KPI, report summary the
   * backend computes in the tenant's base currency), do NOT hand-type a code —
   * use {@link PreferredCurrencyDisplay}, which resolves the reporting base
   * currency the backend summed it in.
   */
  currency: string | null | undefined;
  /** Override the number format instead of using user preference */
  overrideNumberFormat?: NumberFormatOverride;
  /**
   * Decimals shown. Match the backing column: 2 for a NUMERIC(18,2) amount, 4 for
   * a NUMERIC(18,4) per-unit price or per-diem rate. The read-only twin of
   * `LocalizedMonetaryInput`'s `decimalPlaces` — a 4-dp column rendered at 2 dp
   * shows a legitimate 0.0037 per-diem rate as "$0.00", which reads as data loss.
   *
   * `4` means **up to** 4 — the minimum stays the currency's own minor unit, so
   * trailing zeros are suppressed: $12.34 renders "$12.34", not "$12.3400", while
   * $0.0037 keeps all four places. Padding every per-unit price to a fixed 4 dp
   * would claim a precision the value does not have, and it is what made a 4-dp
   * display unacceptable on a customer-facing invoice line. See
   * `docs/design-system/ui-i18n.md` § Currency for the project-wide rule.
   *
   * Left unset, the currency's own minor unit decides (2 for USD/EUR, 0 for JPY),
   * which is right for every amount column — so pass this ONLY for a
   * NUMERIC(18,4) column.
   *
   * `0` is the opposite trade: round to whole currency units to buy horizontal
   * space in a dense list cell (a PO list's Total column). It is a DISPLAY
   * rounding of a value that still carries its cents — never use it where the
   * exact amount is the point (an invoice line, a payment, a totals row), and
   * always pair it with `showTooltip` so the precise figure is one hover away.
   */
  decimalPlaces?: 0 | 2 | 4;
  showTooltip?: boolean;
  emptyText?: string;
  className?: string;
}

/**
 * Format currency with a specific number format
 */
function formatCurrencyWithOverride(
  value: number,
  currency: string,
  numberFormat: NumberFormatOverride,
  // Unset => `style: 'currency'` uses the currency's own minor unit.
  fractionDigits: Intl.NumberFormatOptions | undefined
): string {
  const locale = numberFormat === 'comma_dot' ? 'en-US' : 'de-DE';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    ...fractionDigits,
  }).format(value);
}

/**
 * The currency's own minor unit (2 for USD/EUR, 0 for JPY, 3 for KWD) — the floor
 * a per-unit price is padded to before its extra places are allowed to show.
 * Falls back to 2 for a code `Intl` does not know, and for no code at all — a
 * nullish `currency` reaches here from a NULL currency column, and asking `Intl`
 * about it would throw rather than answer.
 *
 * Memoized per currency code: this runs inside `DataTable` render-cell callbacks
 * (a stock-inquiry page has ~10 4-dp columns across four tables), and constructing
 * an `Intl.NumberFormat` just to read `resolvedOptions()` on every cell would
 * double the formatter allocations per row. The result is a property of the
 * currency, so one lookup per code is all that is ever needed.
 */
const MINOR_UNIT_CACHE = new Map<string, number>();

function currencyMinorUnit(currency: string | null | undefined): number {
  if (!currency) return 2;
  const cached = MINOR_UNIT_CACHE.get(currency);
  if (cached !== undefined) return cached;
  let resolved: number;
  try {
    resolved =
      new Intl.NumberFormat(undefined, { style: 'currency', currency }).resolvedOptions()
        .minimumFractionDigits ?? 2;
  } catch {
    resolved = 2;
  }
  MINOR_UNIT_CACHE.set(currency, resolved);
  return resolved;
}

export function LocalizedCurrencyDisplay({
  value,
  currency,
  overrideNumberFormat,
  decimalPlaces,
  showTooltip = false,
  emptyText = '-',
  className,
}: LocalizedCurrencyDisplayProps) {
  const { formatCurrency } = useUserPreferences();
  // Unset => let `style: 'currency'` use the currency's own minor unit.
  // `4` (a NUMERIC(18,4) per-unit column) => up to 4, floored at the currency's
  // minor unit, so $12.34 does not render as "$12.3400".
  const fractionDigits: Intl.NumberFormatOptions | undefined =
    decimalPlaces === undefined
      ? undefined
      : decimalPlaces === 4
        ? {
            minimumFractionDigits: Math.min(currencyMinorUnit(currency), 4),
            maximumFractionDigits: 4,
          }
        : { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces };

  if (value === null || value === undefined) {
    return <span className={className}>{emptyText}</span>;
  }

  // Guard against empty/invalid currency codes (e.g. before a bank is selected
  // in a create form). Fall back to plain number formatting without a symbol.
  if (!currency) {
    const plain = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...fractionDigits,
    }).format(value);
    return <LtrToken className={className}>{plain}</LtrToken>;
  }

  const formattedCurrency = overrideNumberFormat
    ? formatCurrencyWithOverride(value, currency, overrideNumberFormat, fractionDigits)
    : formatCurrency(value, currency, fractionDigits);

  const content = <LtrToken className={className}>{formattedCurrency}</LtrToken>;

  if (showTooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent>
            <p>{`${value} ${currency}`}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
}

export type PreferredCurrencyDisplayProps = Omit<LocalizedCurrencyDisplayProps, 'currency'>;

/**
 * PreferredCurrencyDisplay - Show a **currency-less** monetary aggregate in the
 * **reporting base currency** — the currency the backend actually summed it in,
 * resolved via `useReportingBaseCurrency()` (active company base → flagged
 * accounting base → tenant default → 'USD', mirroring
 * `FxService.resolve_reporting_base_currency`).
 *
 * This is the one preferred path for dashboard KPIs, report totals, and other
 * cross-document sums the backend computes in the tenant's base currency. It
 * exists so call sites never hand-type a currency for such aggregates.
 *
 * It deliberately does NOT follow `user.preferred_currency`: these totals are
 * never converted at report time (ERP P13), so relabelling one with the viewer's
 * preferred symbol would misrepresent an unconverted number. (The name is
 * historical — it predates that distinction; the anchor is the reporting base.)
 *
 * Do NOT use this for amounts that carry their own currency (a PO, invoice,
 * price-list line, FX transaction) — those must render in their own
 * `currency_code` via `<LocalizedCurrencyDisplay currency={...} />`.
 */
export function PreferredCurrencyDisplay(props: PreferredCurrencyDisplayProps) {
  const currency = useReportingBaseCurrency();
  return <LocalizedCurrencyDisplay {...props} currency={currency} />;
}
